// Where every boat is. ONE placement shared by the Dashboard's "Where the fleet is" strip and the
// Assembly Board's stage load, so both pages always put the same boats in the same stages.
//
// The rule, per boat of the season (after any filter the page applies):
//   1. Delivered: confirmed delivered or marked left on the Overview. Off the floor, so no tick; the
//      pages note them beside the Delivery stage as "+N delivered".
//   2. Idle: GRP has marked it "sent to assembly" (the GRP 2.0 sheet's grpToAssembly), the production
//      sheet has no assembly Start Date for it, and its journey is no further than GRP. It is waiting at
//      Assembly: a low tick there and "+N idle", never counted in a stage (otherwise it would read as
//      "stuck in GRP" for every day since GRP finished).
//   3. Not started: nothing on its journey has begun (Backlog). No tick.
//   4. Everyone else sits at their build journey's current step (journeyOf: the first step not done),
//      so a tick always agrees with the boat's own journey strip. Delivery therefore means "PDI signed
//      off, not yet delivered": ready for delivery.
// Every boat lands in exactly one bucket: staged + idle + not started + delivered = the season's boats.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  JOURNEY_STAGES,
  journeyOf,
  mesStageOf,
  parseOvDate,
  type GrpVessel,
  type JourneyState,
  type JourneyStep,
  type MesStage,
  type OverviewVessel,
  type VesselSummary,
} from '@technohull/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../auth/AuthContext.js';
import { useActiveSeason } from '../season/SeasonContext.js';
import { useOverview, type DataResponse } from './hooks.js';

const DAY = 86_400_000;
const LAST = JOURNEY_STAGES.length - 1;

/** today: the viewer's own calendar date, as UTC midnight. Milestone dates are calendar dates that
 *  parseOvDate reads as UTC midnight of the ISO day, so this is the shape every comparison wants. The
 *  date is the LOCAL one: the UTC date would still be yesterday in Athens between 00:00 and 03:00. */
export const startOfTodayUtc = () => { const n = new Date(); return new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate())); };

/** the two Smartsheet facts that make a boat idle. */
export interface IdleSignal {
  /** SAP → GRP's "sent to assembly" date */
  sent: Map<string, string>;
  /** SAPs whose production sheet has an assembly Start Date (the yard's real "assembly started") */
  started: Set<string>;
}

/** the idle signal from the GRP sheet and (when readable) the production sheets. Without production
 *  data nothing counts as started, exactly as the Board has always read it. */
export function idleSignalFrom(grp: GrpVessel[] | undefined, prod: VesselSummary[] | undefined): IdleSignal {
  const sent = new Map<string, string>();
  for (const g of grp ?? []) if (g.grpToAssembly) sent.set(String(g.sap), g.grpToAssembly);
  const started = new Set<string>();
  for (const p of prod ?? []) if (p.startDate?.trim()) started.add(String(p.sap));
  return { sent, started };
}

export interface PlacedBoat {
  v: OverviewVessel;
  steps: JourneyStep[];
  /** the journey step it sits at (a MES stage key, or 'delivery') and that step's state */
  at: string;
  state: JourneyState;
  /** its production stage (mesStageOf), for dwell days and targets; Delivery boats keep their last one */
  stage: MesStage | null;
}
export interface IdleBoat { v: OverviewVessel; steps: JourneyStep[]; sent: Date; days: number }
export interface StagePlace { key: string; label: string; boats: PlacedBoat[] }
export interface FleetPlacement {
  /** the seven journey stages, GRP → Delivery, each with the boats sitting at it */
  stages: StagePlace[];
  /** waiting at Assembly, longest idle first */
  idle: IdleBoat[];
  /** on the floor, nothing started yet */
  notStarted: OverviewVessel[];
  /** confirmed delivered or gone from the yard */
  delivered: OverviewVessel[];
  /** not delivered, not left: staged + idle + not started */
  onFloor: OverviewVessel[];
  /** boats in a stage (the ticks that count) */
  staged: number;
}

export function placeFleet(vessels: OverviewVessel[], signal: IdleSignal | null, today: Date): FleetPlacement {
  const stages: StagePlace[] = JOURNEY_STAGES.map((s) => ({ key: s.key, label: s.label, boats: [] }));
  const idle: IdleBoat[] = [];
  const notStarted: OverviewVessel[] = [];
  const delivered: OverviewVessel[] = [];
  const onFloor: OverviewVessel[] = [];
  for (const v of vessels) {
    if (v.left || v.deliveryConfirmed) { delivered.push(v); continue; }
    onFloor.push(v);
    const steps = journeyOf(v, today);
    const ci = steps.findIndex((s) => s.state !== 'done');
    const at = ci < 0 ? LAST : ci;
    const state = steps[at]!.state;
    const sap = String(v.sap);
    const sent = signal ? parseOvDate(signal.sent.get(sap)) : null;
    if (sent && at === 0 && !signal!.started.has(sap)) {
      idle.push({ v, steps, sent, days: Math.max(0, Math.round((+today - +sent) / DAY)) });
      continue;
    }
    if (state === 'todo') { notStarted.push(v); continue; }
    stages[at]!.boats.push({ v, steps, at: stages[at]!.key, state, stage: mesStageOf(v, today) });
  }
  idle.sort((a, b) => b.days - a.days);
  return { stages, idle, notStarted, delivered, onFloor, staged: stages.reduce((n, s) => n + s.boats.length, 0) };
}

/** is the idle signal settled? GRP loaded (or failed), and production loaded (or failed) when it was
 *  asked for. Until then a page places no boat as idle rather than flip boats between stages. */
const settled = (q: { data?: unknown; isError: boolean }) => q.data !== undefined || q.isError;
export function idleReady(grp: { data?: unknown; isError: boolean } | null, prod: { data?: unknown; isError: boolean } | null): boolean {
  return (!grp || settled(grp)) && (!prod || settled(prod));
}

/**
 * The Dashboard's placement (the Board feeds placeFleet from its own queries). It reads the same React
 * Query caches as the Board, under the same keys ('grp', ['data', season]), so a visit to either page
 * serves the other, and adds Smartsheet load only when the fleet strip is on screen:
 *   - GRP 2.0 (one sheet) only when `enabled` and the user can read GRP;
 *   - the production sheets (three, one per family) only when GRP reports a boat sent to assembly that
 *     the Overview still has at GRP or not started, i.e. only when they can change the placement.
 * Both are server-cached for 45 s and client-cached for 30 s (the app's staleTime).
 * `ready` is false while those reads are in flight; the strip waits for them so no boat moves after
 * it has been drawn.
 */
export function useFleetPlacement(enabled: boolean) {
  const ov = useOverview();
  const { can } = useAuth();
  const season = useActiveSeason();
  // taken afresh with each Overview load (as the Board does), so a Dashboard left open overnight moves to
  // the new day on its next refetch or Sync
  const today = useMemo(startOfTodayUtc, [ov.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const wantGrp = enabled && can('grp:read');
  // the twin of hooks.ts useGrp / useProductionVessels (same key, same request), with an enabled gate
  const grp = useQuery({
    queryKey: ['grp'],
    queryFn: () => api.get<{ vessels: GrpVessel[] }>('/api/grp'),
    enabled: wantGrp,
  });
  // any boat GRP has sent over that the Overview still has no further than GRP? only then can the
  // production sheets' Start Date decide anything
  const candidates = useMemo(() => {
    if (!grp.data || !ov.data) return false;
    const sent = idleSignalFrom(grp.data.vessels, undefined).sent;
    return ov.data.vessels.some((v) => {
      if (v.left || v.deliveryConfirmed || !parseOvDate(sent.get(String(v.sap)))) return false;
      return journeyOf(v, today).findIndex((s) => s.state !== 'done') === 0;
    });
  }, [grp.data, ov.data, today]);
  const wantProd = wantGrp && candidates && can('production:read');
  const prod = useQuery({
    queryKey: ['data', season],
    queryFn: () => api.get<DataResponse>(`/api/data${season ? `?season=${encodeURIComponent(season)}` : ''}`),
    enabled: wantProd,
  });
  const ready = idleReady(wantGrp ? grp : null, wantProd ? prod : null);
  const placement = useMemo(() => {
    if (!ov.data || !ready) return null;
    const signal = wantGrp && grp.data ? idleSignalFrom(grp.data.vessels, wantProd ? prod.data?.vessels : undefined) : null;
    return placeFleet(ov.data.vessels, signal, today);
  }, [ov.data, ready, wantGrp, wantProd, grp.data, prod.data, today]);
  return { placement, isError: ov.isError };
}
